import React from 'react';
import { IAmendmentGeneralData } from '../line-amendment';
import { IAbstractRecord } from '@models';
import { IAmendmentPageState, IAmendmentPolicyDetails } from '..';
import { cloneDeep } from 'lodash';
import WidgetPaper from '@components/widgets/display-widget/WidgetPaper';
import WidgetSection from '@components/widgets/display-widget/WidgetSection';
import DatePickerFormField from '@components/form-fields/DatePickerFormField';
import SelectFormField from '@components/form-fields/SelectFormField';
import AutocompleteFormField from '@components/form-fields/AutocompleteFormField';
import { useLazyQuery } from '@apollo/client';
import { getAssignedBPPlanListQuery } from '../queries';
import { mapToPlansList } from '../utils';
import { useAppSelector } from '@redux/hooks';
import { isEmpty, isGuid } from '@utils/validationUtils';

interface IAmendmentPolicyDetailsProps {
  generalData: IAmendmentGeneralData;
  lovs: IAbstractRecord;
  pageState: IAmendmentPageState;
  isDisabled: boolean;
  onChange: (pageState: IAmendmentPageState) => void;
}

const AmendmentPolicyDetails: React.FC<IAmendmentPolicyDetailsProps> = ({
  generalData,
  lovs,
  pageState,
  isDisabled,
  onChange,
}) => {
  const user = useAppSelector((state) => state.user);
  const [getAssignedBPPlanListLazy, { loading }] = useLazyQuery(
    getAssignedBPPlanListQuery
  );
  const values = pageState.values.amendmentPolicyDetails;
  const touched = pageState.touched.amendmentPolicyDetails;
  const errors = pageState.errors.amendmentPolicyDetails;

  const handleChange = (
    fieldName: keyof IAmendmentPolicyDetails,
    value: any
  ) => {
    const newState = cloneDeep(pageState);
    newState.values.amendmentPolicyDetails[fieldName] = value;
    onChange(newState);
  };

  return (
    <WidgetPaper>
      <WidgetSection title="Policy Details" isFieldsGrid>
        <AutocompleteFormField
          key="plan"
          name="plan"
          title="Plan"
          placeholder="Plan"
          value={values.plan}
          error={touched.plan ? errors.plan : ''}
          loader={loading}
          disabled={isDisabled}
          onChange={(v) => handleChange('plan', v)}
          material
          fetchOptions={async (
            value,
            pagination
          ): Promise<{
            options: Record<string, string>;
            totalCount: number;
          }> => {
            try {
              const { data } = await getAssignedBPPlanListLazy({
                variables: {
                  pagination,
                  KeywordSearch: isGuid(value) ? generalData.plan.name : value,
                  selectedBusinessUserID: user.info.businessUser_Id,
                  selectedLineID: [generalData.line.id],
                },
                errorPolicy: 'all',
              });

              const options = mapToPlansList(data);
              if (!options[value] && !isEmpty(value)) {
                options[value] = generalData.plan.name;
              }

              return {
                options,
                totalCount:
                  data?.Production?.queries?.getAssignedBPPlanList?.paging
                    ?.totalCount,
              };
            } catch (error) {
              return {
                options: {
                  [value]: generalData.plan.name,
                },
                totalCount: 1,
              };
            }
          }}
        />
        <DatePickerFormField
          name="policyEffectiveDate"
          title="Policy Effective Date"
          value={values.policyEffectiveDate}
          error={touched.policyEffectiveDate ? errors.policyEffectiveDate : ''}
          onDateChange={(v) => handleChange('policyEffectiveDate', v)}
          disabled={
            isDisabled || generalData.amendment.type != 'POLICY_CORRECTION'
          }
          material
        />
        <DatePickerFormField
          name="amendmentExpiryDate"
          title="Amended Policy Expiry Date"
          value={values.amendmentExpiryDate}
          error={touched.amendmentExpiryDate ? errors.amendmentExpiryDate : ''}
          disabled={isDisabled}
          onDateChange={(v) => handleChange('amendmentExpiryDate', v)}
          minDate={new Date(generalData.policy.effectiveDate)}
          maxDate={new Date(generalData.policy.expiryDate)}
          material
        />
        <SelectFormField
          key="policyCurrency"
          name="policyCurrency"
          title="Policy Currency"
          placeholder="Policy Currency"
          value={values.policyCurrency}
          disabled={isDisabled}
          error={touched.policyCurrency ? errors.policyCurrency : ''}
          onChange={(v) => handleChange('policyCurrency', v)}
          material
          injectGlobalLov={{
            type: 'currency',
            value: 'Code',
            display: 'Symbol',
          }}
        />
      </WidgetSection>
    </WidgetPaper>
  );
};

export default AmendmentPolicyDetails;
